import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { Expression } from 'mapbox-gl';
import { Layer, Source } from 'react-map-gl';
import { useParams } from 'react-router-dom';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { useMatchCurrentPage } from '@/hooks/useMatchCurrentPage';
import { usePathSet } from '@/hooks/usePathSet';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { LayerProps, MAP_ORIGIN, RasterProps, SIEMENS_HACK_PROTECTED_AREAS_LAYER } from './constants';
import { useControlsContext, useMapSelectedLayer } from './hooks/useFormControls';
import { useIsSiemensOrganization } from './hooks/useIsSiemensOrganization';
import { useLayerOpacity } from './hooks/useLayerOpacity';
import { getMapEndpoint, useMapEndpointOrg, useMapEndpointPlot } from './hooks/useMapEndpoint';
import { useSiemensData } from './hooks/useSiemensData';

const PROTECTED_AREAS_LAYER_ID = 'pa-layer';

export const RasterSourceLayer = ({
  layer,
  legendFilter,
}: {
  layer: LayerProps | undefined;
  legendFilter?: string | null;
}) => {
  const pathSet = usePathSet();
  const isPlotPath = useMatchCurrentPage([pathSet.plot, pathSet.plotLandMonitoring, pathSet.plotOverview]);

  if (isPlotPath) {
    return <RasterSourceLayerPlot layer={layer} legendFilter={legendFilter} />;
  }

  return <RasterSourceLayerOrg layer={layer} legendFilter={legendFilter} />;
};

const RasterSourceLayerPlot = ({
  layer,
  legendFilter,
}: {
  layer: LayerProps | undefined;
  legendFilter?: string | null;
}) => {
  const controlsForm = useControlsContext();
  const [year] = controlsForm.watch(['year']);

  const rasterLayerUrl = useMapEndpointPlot(layer, year, legendFilter);

  return <RasterSourceLayerBase layer={layer} rasterLayerUrl={rasterLayerUrl} />;
};

const RasterSourceLayerOrg = ({
  layer,
  legendFilter,
}: {
  layer: LayerProps | undefined;
  legendFilter?: string | null;
}) => {
  const controlsForm = useControlsContext();
  const [year] = controlsForm.watch(['year']);

  const rasterLayerUrl = useMapEndpointOrg(layer, year, legendFilter);

  return <RasterSourceLayerBase layer={layer} rasterLayerUrl={rasterLayerUrl} />;
};

const RasterSourceLayerBase = ({
  layer,
  rasterLayerUrl,
}: {
  layer: LayerProps | undefined;
  rasterLayerUrl: string;
}) => {
  const controlsForm = useControlsContext();
  const [year] = controlsForm.watch(['year']);
  const isSiemensOrganization = useIsSiemensOrganization();

  const layerOpacity = useLayerOpacity();

  const layerName = layer?.value ?? '';

  const layerOrigin = layer?.origin || MAP_ORIGIN.cckp;

  if (layerName === MAP_LAYER_FACTS.dw_lulc) {
    return <LandcoverLayer url={rasterLayerUrl} />;
  }

  if (layerName === MAP_LAYER_FACTS.combined_ibat_lulc_urls) {
    return (
      <>
        {isSiemensOrganization ? <SiemensProtectedAreasBorderLayer /> : <IucnProtectedAreasBorderLayer />}
        <LandcoverLayer url={rasterLayerUrl} beforeId={PROTECTED_AREAS_LAYER_ID} />
      </>
    );
  }

  if (layerName === MAP_LAYER_FACTS.combined_osm_lulc) {
    return (
      <>
        <OsmBorderLayer />
        <LandcoverLayer url={rasterLayerUrl} beforeId='heatmap-protected-areas-layer-raster' />
      </>
    );
  }

  return (
    <Source
      key={`${layer?.layerSubType ?? layerName}-${year}-${rasterLayerUrl}`} // Force re-render when layer or year changes
      id={`heatmap-source-${layer?.layerSubType ?? layerName}`}
      type='raster'
      tiles={[rasterLayerUrl]}
      tileSize={256}
      scheme={layerOrigin === MAP_ORIGIN.gbif ? 'xyz' : 'tms'}
    >
      <Layer
        id={`heatmap-layer-raster-${layer?.layerSubType ?? layerName}`}
        type='raster'
        paint={{
          'raster-opacity': layerOpacity,
        }}
      />
    </Source>
  );
};

export const GeojsonSourceLayer = ({
  layer,
  legendFilter,
}: {
  layer: LayerProps | undefined;
  legendFilter?: string | null;
}) => {
  const routeParams = useParams();

  const pathSet = usePathSet();
  const isPlotPath = useMatchCurrentPage([pathSet.plot, pathSet.plotLandMonitoring, pathSet.plotOverview]);

  if (!layer) {
    return null;
  }

  if (!routeParams.plotId) {
    return null;
  }
  // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
  if (layer.value === MAP_LAYER_FACTS.siemens_protected_areas) {
    return <SiemensProtectedAreasBorderLayer />;
  }

  if (isPlotPath) {
    return <GeojsonSourceLayerPlot selectedLayer={layer} legendFilter={legendFilter} />;
  }

  return <GeojsonSourceLayerOrg selectedLayer={layer} legendFilter={legendFilter} />;
};

const GeojsonSourceLayerPlot = ({
  selectedLayer,
  legendFilter,
}: {
  selectedLayer: LayerProps;
  legendFilter?: string | null;
}) => {
  const geojsonFact = usePlotFacts([selectedLayer.value]);
  const geojsonData = getFactFromFactArray(geojsonFact, selectedLayer.value);

  const [key, filter] = legendFilter ? legendFilter.split('=') : [];

  let geojsonLayer = geojsonData?.value as FeatureCollection<Geometry, GeoJsonProperties> | undefined;

  if (legendFilter && key && filter && geojsonLayer) {
    const parsedFilter = filter.replace(/'/g, '');

    geojsonLayer = {
      ...geojsonLayer,
      features: geojsonLayer?.features?.filter((feature) => feature?.properties?.[`${key}`] === parsedFilter),
    };
  }

  return <GeojsonSourceLayerBase selectedLayer={selectedLayer} geojsonLayer={geojsonLayer} />;
};

const GeojsonSourceLayerOrg = ({
  selectedLayer,
  legendFilter,
}: {
  selectedLayer: LayerProps;
  legendFilter?: string | null;
}) => {
  const geojsonData = useOrganizationIndicatorReportingFact(selectedLayer.value);

  const [key, filter] = legendFilter ? legendFilter.split('=') : [];

  let geojsonLayer = geojsonData?.[selectedLayer.value]?.value as
    | FeatureCollection<Geometry, GeoJsonProperties>
    | undefined;

  if (legendFilter && key && filter && geojsonLayer) {
    const parsedFilter = filter.replace(/'/g, '');

    geojsonLayer = {
      ...geojsonLayer,
      features: geojsonLayer?.features?.filter((feature) => feature?.properties?.[`${key}`] === parsedFilter),
    };
  }

  return <GeojsonSourceLayerBase selectedLayer={selectedLayer} geojsonLayer={geojsonLayer} />;
};

const GeojsonSourceLayerBase = ({
  selectedLayer,
  geojsonLayer,
}: {
  selectedLayer: LayerProps;
  geojsonLayer: FeatureCollection<Geometry, GeoJsonProperties> | undefined;
}) => {
  const controlsForm = useControlsContext();

  if (!geojsonLayer) {
    return null;
  }

  const [layer, year] = controlsForm.watch(['layer', 'year']);

  return (
    <Source
      key={`${layer}-${year}`} // Force re-render when layer or year changes
      id='heatmap-source-geojson'
      type='geojson'
      data={geojsonLayer}
    >
      {selectedLayer.raster && <RasterLayer {...(selectedLayer.raster as RasterProps)} />}
    </Source>
  );
};

const RasterLayer = (props: RasterProps) => {
  switch (props.type) {
    case 'circle':
      return <CircleLayer {...props} />;
    case 'line':
      return <LineLayer {...props} />;
    default:
      return null;
  }
};

const CircleLayer = ({ colors, dataKey }: RasterProps) => {
  if (isNullish(colors) || isNullish(dataKey)) {
    return null;
  }
  const colorExpression = [
    'case',
    ...Object.entries(colors).flatMap(([key, color]) => [['==', ['get', dataKey], key], color]),
    '#cccccc', // Fallback color
  ] as Expression;

  return (
    <Layer
      id='heatmap-layer'
      source='heatmap-source-geojson'
      type='circle'
      paint={{
        'circle-color': colorExpression,
        'circle-radius': 4,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
      }}
    />
  );
};

const LineLayer = ({ color = '#cccccc', width = 2 }: RasterProps) => {
  return (
    <Layer
      id='heatmap-layer'
      source='heatmap-source-geojson'
      type='line'
      paint={{
        'line-color': color,
        'line-width': width,
      }}
    />
  );
};

const LandcoverLayer = ({ url, beforeId }: { url: string; beforeId?: string }) => {
  const controlsForm = useControlsContext();
  const year = controlsForm.watch('year');

  const layerOpacity = useLayerOpacity();

  return (
    <Source
      key={`ibat-${url}-${year}`}
      id='heatmap-source-ibat'
      type='raster'
      tiles={[url]}
      tileSize={256}
      scheme='tms'
    >
      <Layer
        id='heatmap-layer-raster-ibat'
        type='raster'
        beforeId={beforeId}
        paint={{
          'raster-opacity': layerOpacity,
        }}
      />
    </Source>
  );
};

const OsmBorderLayer = () => {
  const controlsForm = useControlsContext();
  const year = controlsForm.watch('year');

  const layerOpacity = useLayerOpacity();

  const selectedLayer = useMapSelectedLayer();
  const layerOrigin = selectedLayer?.origin || MAP_ORIGIN.landscape;

  const protectedAreasUrl = `${getMapEndpoint(layerOrigin, MAP_LAYER_FACTS.osm_protected_areas, year)}&styles=${MAP_LAYER_FACTS.osm_protected_areas}_border`;
  return (
    <>
      <Source
        key={`osm${protectedAreasUrl}-${year}`} // Force re-render when layer or year changes
        id='heatmap-protected-areas-source'
        type='raster'
        tiles={[protectedAreasUrl]}
        tileSize={256}
        scheme='tms'
      >
        <Layer
          id='heatmap-protected-areas-layer-raster'
          type='raster'
          paint={{
            'raster-opacity': layerOpacity,
          }}
        />
      </Source>
    </>
  );
};

const IucnProtectedAreasBorderLayer = () => {
  const controlsForm = useControlsContext();
  const year = controlsForm.watch('year');

  const layerOpacity = useLayerOpacity();

  const protectedAreasUrl = `${getMapEndpoint(MAP_ORIGIN.landscape, MAP_LAYER_FACTS.iucn_protected_areas, year)}&styles=${MAP_LAYER_FACTS.iucn_protected_areas}_border`;

  return (
    <>
      <Source
        key={`iucn${protectedAreasUrl}-${year}`} // Force re-render when layer or year changes
        id='heatmap-protected-areas-source'
        type='raster'
        tiles={[protectedAreasUrl]}
        tileSize={256}
        scheme='tms'
      >
        <Layer
          id={PROTECTED_AREAS_LAYER_ID}
          type='raster'
          paint={{
            'raster-opacity': layerOpacity,
          }}
        />
      </Source>
    </>
  );
};

// TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
const SiemensProtectedAreasBorderLayer = () => {
  const data = useSiemensData();
  const controlsForm = useControlsContext();
  const layer = controlsForm.watch('layer');

  const safeData = data || { type: 'FeatureCollection', features: [] };

  return (
    <Source
      key={`${layer}-${MAP_LAYER_FACTS.siemens_protected_areas}`}
      id='heatmap-source-geojson'
      type='geojson'
      data={safeData}
    >
      <Layer
        id={PROTECTED_AREAS_LAYER_ID}
        type='line'
        paint={{
          'line-color': SIEMENS_HACK_PROTECTED_AREAS_LAYER.raster.color,
          'line-width': SIEMENS_HACK_PROTECTED_AREAS_LAYER.raster.width,
        }}
      />
    </Source>
  );
};
